import React, { Component } from "react";
import {
  getEligibleFusionStrategies,
  getPrimaryRegistrationValue,
  shouldForceTaxableForRegistration,
  isIspOnlyProgram,
  getFusionAllocationDisplayName,
  calculateFusionWeightedRiskScore,
} from "../../utils/proposalUtils";
import { validateManagementFeeTable, sortFeeTiers } from "./fusionFeeUtils";
import { normalizeIarFeeTiered, setTieredRate } from "./iarFeeTieredUtils";

// Fallback fee schedule if API hasn't loaded yet
const DEFAULT_FEE_SCHEDULES = {
  "Schedule 1": [
    "First $100,000",
    "Next $150,000",
    "Next $250,000",
    "Next $500,000",
    "Next $1,000,000",
    "Next $2,000,000",
    "Over $4,000,000",
  ],
};

const getTaxSensitivityOptions = (proposalType, forceTaxable = false) => {
  const options = [];
  if (Array.isArray(proposalType?.taxableBonds) && proposalType.taxableBonds.length > 0) {
    options.push("Taxable");
  }
  if (
    !forceTaxable &&
    Array.isArray(proposalType?.municipalBonds) &&
    proposalType.municipalBonds.length > 0
  ) {
    options.push("Municipal");
  }
  return options;
};

const getRiskToleranceOptions = (proposalType, taxSensitivity) => {
  if (taxSensitivity === "Municipal") {
    return proposalType?.municipalBonds || [];
  }
  if (taxSensitivity === "Taxable") {
    return proposalType?.taxableBonds || [];
  }
  return [
    ...(proposalType?.taxableBonds || []),
    ...(proposalType?.municipalBonds || []),
  ];
};

class ISPFeeSchedule extends Component {
  isManagedMoneyMarketStrategy = () =>
    String(this.props.strategy || "")
      .toLowerCase()
      .includes("managed money market solution");

  getIarFeeMax = () => (this.isManagedMoneyMarketStrategy() ? 0.15 : 2);

  // Get fee schedules from customFees API response, fallback to default
  getFeeSchedules = () => {
    const customFees = this.props.customFees;
    // Use feeSchedules map from API (each key is a schedule name, value is array of {label, rate})
    if (
      customFees?.feeSchedules &&
      typeof customFees.feeSchedules === "object"
    ) {
      // Convert DynamoDB format [{label, rate}] to just label strings for the UI
      const schedules = {};
      for (const [name, tiers] of Object.entries(customFees.feeSchedules)) {
        if (Array.isArray(tiers)) {
          schedules[name] = sortFeeTiers(tiers.map((t) => t.label || t));
        }
      }
      if (Object.keys(schedules).length > 0) return schedules;
    }
    // Legacy: single tiers array (convert to Schedule 1)
    if (customFees?.tiers && Array.isArray(customFees.tiers)) {
      return {
        "Schedule 1": sortFeeTiers(customFees.tiers.map((t) => t.label || t)),
      };
    }
    return DEFAULT_FEE_SCHEDULES;
  };

  renderFeeTable = (tieredRates, title, subtitle) => (
    <div className="form-group mt-3">
      <label>
        <strong>{title}</strong>{" "}
        {subtitle && (
          <span style={{ fontSize: "12px", color: "#6c757d" }}>
            ({subtitle})
          </span>
        )}
      </label>
      {/* Scrollable wrapper keeps a long tier table from pushing the rest of
          Section 4 below the fold; user can scroll within the frame. No border
          here — the table's own `table-bordered` is the single visible box, so
          a short (non-scrolling) table doesn't show a double border. */}
      <div
        style={{
          maxHeight: "320px",
          overflowY: "auto",
          maxWidth: "452px",
          width: "fit-content",
        }}
      >
        <table
          className="table table-sm table-bordered mb-0"
          style={{ maxWidth: "450px" }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <tr>
              <th>Tier</th>
              <th style={{ width: "120px" }}>Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            {tieredRates.map((tier, index) => (
              <tr key={index}>
                <td style={{ verticalAlign: "middle" }}>{tier.label}</td>
                <td
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    verticalAlign: "middle",
                  }}
                >
                  {Number(tier.rate).toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  renderFlatFeeTable = () => {
    const flatFee = this.props.managementFees?.flatFee;
    if (flatFee === undefined || flatFee === null || flatFee === "")
      return null;
    return this.renderFeeTable(
      [{ label: "All assets", rate: Number(flatFee) }],
      "Fixed Percentage - All Assets",
      "Program Fee — Read Only",
    );
  };

  renderStandardFees = () => {
    if (this.props.managementFeesLoading) {
      return (
        <div
          className="form-group mt-3 text-center"
          style={{ maxWidth: "450px" }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <span className="ml-2" style={{ fontSize: "13px", color: "#6c757d" }}>
            Loading fee schedule...
          </span>
        </div>
      );
    }
    const fees = this.props.managementFees;
    const flatTable = this.renderFlatFeeTable();
    const hasTiers = fees?.tieredRates?.length > 0;
    // Path mode: a flat-fee strategy whose path target's row
    // was injected into fees by ispForm.fetchManagementFees. Signalled by the
    // pathMinimumFee carry-along, never set in any other path.
    const pathMin = fees?.pathMinimumFee;
    const inPathMode = flatTable != null && hasTiers && pathMin != null;
    const noteStyle = {
      fontSize: "12px",
      color: "#6c757d",
      fontWeight: 700,
      marginTop: "-8px",
    };

    if (flatTable || hasTiers) {
      return (
        <div>
          {/* Fixed percentage renders first — it applies now, before any
              path event. Mirrors the generated document's section order. */}
          {flatTable}
          {flatTable && fees?.minimumFee && (
            <div style={noteStyle}>
              Minimum account fee of ${fees.minimumFee} applies to the Fixed
              Percentage. The greater of the minimum or the Fixed Percentage
              will be charged to the account.
            </div>
          )}
          {inPathMode && (
            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                fontStyle: "italic",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              After meeting the $25,000 threshold (as described in the Strategy
              Description), the Investment Management Fee converts to the tiered
              schedule shown below.
            </div>
          )}
          {hasTiers &&
            this.renderFeeTable(
              fees.tieredRates,
              "Investment Management Fee",
              "Program Fee — Read Only",
            )}
          {hasTiers && inPathMode && (
            <div style={noteStyle}>
              After meeting the $25,000 threshold, a minimum account fee of
              ${pathMin} applies. The greater of the minimum account fee or the
              Investment Management Fee percentage will be charged to the
              account.
            </div>
          )}
          {hasTiers && !inPathMode && !flatTable && fees?.minimumFee && (
            <div style={noteStyle}>
              Minimum account fee of ${fees.minimumFee} applies. The greater of
              the minimum account fee or the Investment Management Fee
              percentage will be charged to the account.
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className="form-group mt-3"
        style={{ borderLeft: "5px solid #dc3545", paddingLeft: "12px" }}
      >
        <label>
          <strong>Investment Management Fee</strong>
        </label>
        <div style={{ fontSize: "13px", color: "#dc3545" }}>
          No fee schedule found for this strategy and tier combination. Please
          verify the selected tier is correct.
        </div>
      </div>
    );
  };

  renderFusionFees = () => {
    const {
      fusionAllocations,
      lists,
      onFusionAllocationsChange,
      bondType,
      registrations,
      values,
      fusionRegistrationType,
      jointClients,
    } = this.props;
    const allocations = fusionAllocations || [];
    const isFusionMultiRegistration = fusionRegistrationType === "Multi";
    const accountOwnerOptions = (jointClients || [])
      .map((client) =>
        client?.clientName ||
        [client?.firstName, client?.middleName, client?.lastName]
          .filter(Boolean)
          .join(" ")
      )
      .filter(Boolean);
    const registrationOptions = Array.from(
      new Set((lists?.registrationTypes || []).map((type) => type.name).filter(Boolean))
    ).sort();
    const primaryRegistrationValue = getPrimaryRegistrationValue(registrations);
    const forceTaxable = shouldForceTaxableForRegistration(
      primaryRegistrationValue,
      lists?.registrationTypes || [],
    );
    const preferredTaxSensitivity = forceTaxable ? "Taxable" : bondType || "Taxable";
    const proposalTypes = lists?.proposalTypes || [];
    const availableStrategyOptions = getEligibleFusionStrategies(
      proposalTypes,
      { allowIspOnly: true },
    )
      .filter((proposalType) =>
        getTaxSensitivityOptions(proposalType, forceTaxable).length > 0
      )
      .sort((a, b) => a.name.localeCompare(b.name));
    const proposalTypeByName = new Map(
      proposalTypes.map((proposalType) => [proposalType.name, proposalType])
    );

    const total = allocations.reduce(
      (sum, a) => sum + (parseFloat(a.percentage) || 0),
      0,
    );
    const isComplete = Math.abs(total - 100) < 0.01 && allocations.length > 0;
    const weightedRiskScore = calculateFusionWeightedRiskScore(
      allocations,
      proposalTypes,
    );
    const hasWeightedRiskScore = weightedRiskScore.score !== null;
    const weightedRiskScoreText = hasWeightedRiskScore
      ? `${(Math.round(weightedRiskScore.score * 100) / 100).toFixed(2)} (${weightedRiskScore.category})`
      : "N/A";
    const MAX_FUSION_ALLOCATIONS = 8;
    const isAtMaxAllocations = allocations.length >= MAX_FUSION_ALLOCATIONS;
    const enrichAllocation = (allocation) => ({
      ...allocation,
      displayName: getFusionAllocationDisplayName(allocation, proposalTypes),
    });
    const handleAdd = () => {
      if (isAtMaxAllocations) return;
      onFusionAllocationsChange([
        ...allocations,
        {
          strategy: "",
          riskTolerance: "",
          taxSensitivity: "",
          registration: "",
          accountOwner: "",
          displayName: "",
          percentage: "",
        },
      ]);
    };
    const handleRemove = (index) =>
      onFusionAllocationsChange(allocations.filter((_, i) => i !== index));
    const handleChange = (index, field, value) =>
      onFusionAllocationsChange(
        allocations.map((a, i) =>
          i === index ? enrichAllocation({ ...a, [field]: value }) : a
        ),
      );
    const handleStrategyChange = (index, strategyName) => {
      const proposalType = proposalTypeByName.get(strategyName);
      const taxOptions = getTaxSensitivityOptions(proposalType, forceTaxable);
      const taxSensitivity = taxOptions.includes(preferredTaxSensitivity)
        ? preferredTaxSensitivity
        : taxOptions[0] || "";
      const riskOptions = getRiskToleranceOptions(proposalType, taxSensitivity);
      const current = allocations[index] || {};
      onFusionAllocationsChange(
        allocations.map((allocation, i) =>
          i === index
            ? enrichAllocation({
                ...current,
                strategy: strategyName,
                taxSensitivity,
                riskTolerance:
                  riskOptions.length === 1 ? riskOptions[0] : "",
              })
            : allocation
        ),
      );
    };
    const handleTaxSensitivityChange = (index, taxSensitivity) => {
      const current = allocations[index] || {};
      const proposalType = proposalTypeByName.get(current.strategy);
      const riskOptions = getRiskToleranceOptions(proposalType, taxSensitivity);
      const riskTolerance = riskOptions.includes(current.riskTolerance)
        ? current.riskTolerance
        : "";
      onFusionAllocationsChange(
        allocations.map((allocation, i) =>
          i === index
            ? enrichAllocation({
                ...current,
                taxSensitivity,
                riskTolerance,
              })
            : allocation
        ),
      );
    };

    return (
      <div className="form-group mt-3">
        <label>
          <strong>Fusion Sub-Strategy Allocations</strong>
        </label>
        <small className="form-text text-muted mb-2">
          Select the investment programs within Fusion and assign percentage
          allocations totaling 100%.
        </small>

        {allocations.map((alloc, index) => {
          const feeKey = values.selectedManagementFeeTierKey
            ? `${alloc.strategy}|${values.selectedManagementFeeTierKey}`
            : "";
          const feeValidation = validateManagementFeeTable(
            this.props.fusionSubFees?.[feeKey],
          );
          const hasFeeData = !!(
            values.selectedManagementFeeTierKey &&
            alloc.strategy &&
            feeValidation.isComplete
          );
          const limitError =
            this.props.fusionAllocationLimitErrors?.[index] || null;
          const rowComplete =
            values.selectedManagementFeeTierKey &&
            alloc.strategy &&
            alloc.riskTolerance &&
            alloc.taxSensitivity &&
            (!isFusionMultiRegistration ||
              (alloc.registration && alloc.accountOwner)) &&
            parseFloat(alloc.percentage) > 0 &&
            hasFeeData &&
            !limitError;
          const selectedProposalType = proposalTypeByName.get(alloc.strategy);
          const taxSensitivityOptions = getTaxSensitivityOptions(
            selectedProposalType,
            forceTaxable,
          );
          const riskToleranceOptions = getRiskToleranceOptions(
            selectedProposalType,
            alloc.taxSensitivity,
          );
          const strategyOptions = [...availableStrategyOptions];
          if (
            alloc.strategy &&
            !strategyOptions.some((proposalType) => proposalType.name === alloc.strategy)
          ) {
            strategyOptions.unshift({ name: alloc.strategy });
          }
          const allocationDisplayName =
            alloc.displayName ||
            getFusionAllocationDisplayName(alloc, proposalTypes);
          return (
            <div
              key={index}
              className="mb-2"
              style={{
                borderLeft: `5px solid ${rowComplete ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              <div className="form-row align-items-end">
                <div className="form-group col-md-4 mb-2">
                  <label htmlFor={`fusionStrategy${index}`}>
                    Strategy
                  </label>
                  <select
                    id={`fusionStrategy${index}`}
                    className="form-control"
                    value={alloc.strategy || ""}
                    onChange={(e) =>
                      handleStrategyChange(index, e.target.value)
                    }
                  >
                    <option value="">Select Strategy</option>
                    {strategyOptions.map((proposalType) => (
                      <option key={proposalType.name} value={proposalType.name}>
                        {proposalType.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-3 mb-2">
                  <label htmlFor={`fusionRiskTolerance${index}`}>
                    Risk Tolerance
                  </label>
                  <select
                    id={`fusionRiskTolerance${index}`}
                    className="form-control"
                    value={alloc.riskTolerance || ""}
                    disabled={!alloc.strategy || riskToleranceOptions.length === 0}
                    onChange={(e) =>
                      handleChange(index, "riskTolerance", e.target.value)
                    }
                  >
                    <option value="">Select Risk</option>
                    {riskToleranceOptions.map((riskTolerance) => (
                      <option key={riskTolerance} value={riskTolerance}>
                        {riskTolerance}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-2 mb-2">
                  <label htmlFor={`fusionTaxSensitivity${index}`}>
                    Tax Sensitivity
                  </label>
                  <select
                    id={`fusionTaxSensitivity${index}`}
                    className="form-control"
                    value={alloc.taxSensitivity || ""}
                    disabled={!alloc.strategy || taxSensitivityOptions.length === 0}
                    onChange={(e) =>
                      handleTaxSensitivityChange(index, e.target.value)
                    }
                  >
                    <option value="">Select Tax</option>
                    {taxSensitivityOptions.map((taxSensitivity) => (
                      <option key={taxSensitivity} value={taxSensitivity}>
                        {taxSensitivity}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-2 mb-2">
                  <label htmlFor={`fusionWeight${index}`}>Weight</label>
                  <div className="input-group">
                    <input
                      id={`fusionWeight${index}`}
                      type="number"
                      className="form-control"
                      value={alloc.percentage}
                      min="0"
                      max="100"
                      step="1"
                      placeholder="0"
                      onChange={(e) => {
                        const val = e.target.value;
                        if (
                          val === "" ||
                          (/^(?:0|[1-9]\d*)$/.test(val) && Number(val) <= 100)
                        ) {
                          handleChange(index, "percentage", val);
                        }
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                </div>
                <div className="form-group col-md-1 mb-2">
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ padding: "6px 8px", marginTop: "31px" }}
                    onClick={() => handleRemove(index)}
                    title="Remove"
                  >
                    <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                  </button>
                </div>
              </div>
              {allocationDisplayName && (
                <div
                  className="text-muted"
                  style={{ fontSize: "12px", marginTop: "-2px" }}
                >
                  Allocation Model: {allocationDisplayName}
                </div>
              )}
              {isFusionMultiRegistration && (
                <div className="form-row mt-2">
                  <div className="form-group col-md-4 mb-2">
                    <label htmlFor={`fusionAccountOwner${index}`}>
                      Account Owner
                    </label>
                    <select
                      id={`fusionAccountOwner${index}`}
                      className="form-control"
                      value={alloc.accountOwner || ""}
                      onChange={(e) =>
                        handleChange(index, "accountOwner", e.target.value)
                      }
                    >
                      <option value="">Select Account Owner</option>
                      {accountOwnerOptions.map((owner) => (
                        <option key={owner} value={owner}>
                          {owner}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-4 mb-2">
                    <label htmlFor={`fusionRegistration${index}`}>
                      Registration
                    </label>
                    <select
                      id={`fusionRegistration${index}`}
                      className="form-control"
                      value={alloc.registration || ""}
                      onChange={(e) =>
                        handleChange(index, "registration", e.target.value)
                      }
                    >
                      <option value="">Select Registration</option>
                      {registrationOptions.map((registration) => (
                        <option key={registration} value={registration}>
                          {registration}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {limitError && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#dc3545",
                    marginTop: "4px",
                  }}
                >
                  {limitError}
                </div>
              )}
              {alloc.strategy &&
                isIspOnlyProgram(alloc.strategy, lists?.proposalTypes) && (
                  <div
                    role="alert"
                    style={{
                      fontSize: "12px",
                      color: "#b8860b",
                      marginTop: "4px",
                    }}
                  >
                    {alloc.strategy} is an ISP-only strategy. This ISP can be
                    generated, but it requires a custom proposal — a generated
                    proposal cannot be created from it.
                  </div>
                )}
            </div>
          );
        })}

        <div className="d-flex align-items-center mt-2 mb-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleAdd}
            disabled={isAtMaxAllocations}
            title={
              isAtMaxAllocations
                ? `Maximum of ${MAX_FUSION_ALLOCATIONS} sub-strategies reached`
                : undefined
            }
          >
            + Add Sub-Strategy
          </button>
          <span className="ml-3 text-muted" style={{ fontSize: "13px" }}>
            ({allocations.length}/{MAX_FUSION_ALLOCATIONS})
          </span>
          {allocations.length > 0 && (
            <span
              className="ml-3"
              style={{
                fontWeight: "bold",
                color: isComplete ? "#28a745" : "#dc3545",
              }}
            >
              Total: {total.toFixed(0)}%
            </span>
          )}
        </div>

        {allocations.length > 0 && (
          <div
            className="mb-3"
            style={{
              fontSize: "13px",
              color: hasWeightedRiskScore ? "#495057" : "#6c757d",
              fontWeight: 600,
            }}
          >
            Weighted Risk Score: {weightedRiskScoreText}
            {weightedRiskScore.hasMissingScore && (
              <div
                style={{
                  color: "#dc3545",
                  fontWeight: 400,
                  marginTop: "2px",
                }}
              >
                Risk score data is missing for one or more selected sub-strategies.
              </div>
            )}
          </div>
        )}

        {/* T4 (round 4) — show every sub-strategy's full fee schedule
            individually instead of a blended/weighted-average table.
            Stakeholder requirement: every sleeve's tier schedule must
            appear on the ISP for compliance, since households can change
            mid-application.

            The outer container keeps a *stable* height (fixed maxHeight,
            generous minHeight) whether we're showing the loading spinner or
            the loaded tables, so the async sub-strategy fee fetch finishing
            doesn't shove the IAR Fee section below it down the page and
            yank the cursor away from whatever the user is typing. */}
        {isComplete &&
          (this.props.fusionSubFeesLoading ||
            this.props.fusionFeeBlocks?.length > 0) && (
            <div>
              <label className="d-block mt-3 mb-1">
                <strong>
                  Sub-Strategy Investment Management Fee Schedules
                </strong>
                <small className="text-muted ml-2">
                  (per sub-strategy — Read Only)
                </small>
              </label>
              <div
                style={{
                  minHeight: "220px",
                  maxHeight: "420px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "12px 14px",
                  backgroundColor: "#fcfcfd",
                }}
              >
                {this.props.fusionSubFeesLoading ? (
                  <div className="text-center" style={{ padding: "32px 0" }}>
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    />
                    <span
                      className="ml-2"
                      style={{ fontSize: "13px", color: "#6c757d" }}
                    >
                      Loading sub-strategy fee schedules...
                    </span>
                  </div>
                ) : (
                  this.props.fusionFeeBlocks.map((block, idx) => (
                    <div key={idx} className="mb-3">
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#385EA4",
                          marginBottom: "4px",
                        }}
                      >
                        {block.displayName || block.strategy}
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#6c757d",
                            marginLeft: "8px",
                          }}
                        >
                          {block.weight !== "" &&
                          block.weight !== null &&
                          block.weight !== undefined
                            ? `Allocation: ${Number(block.weight).toFixed(0)}%`
                            : ""}
                          {block.tier && block.tier !== "Standard"
                            ? `${block.weight !== "" ? "   •   " : ""}Tier: ${block.tier}`
                            : ""}
                        </span>
                      </div>
                      {block.tieredRates?.length > 0 ? (
                        <table
                          className="table table-sm table-bordered mb-1"
                          style={{ maxWidth: "450px" }}
                        >
                          <thead>
                            <tr>
                              <th>Asset Level</th>
                              <th style={{ width: "120px" }}>Annual Fee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {block.tieredRates.map((tier, i) => (
                              <tr key={i}>
                                <td style={{ verticalAlign: "middle" }}>
                                  {tier.label}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: "#f8f9fa",
                                    color: "#495057",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {Number(tier.rate).toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ fontSize: "12px", color: "#dc3545" }}>
                          No tier schedule found for this sub-strategy at the
                          selected tier.
                        </div>
                      )}
                      {block.minimumFee && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            fontStyle: "italic",
                          }}
                        >
                          *Minimum account fee of ${block.minimumFee} applies
                          for this sub-strategy.
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        {!this.props.fusionSubFeesLoading &&
          this.props.fusionFeeValidation?.issues?.length > 0 && (
            <div
              className="mt-3"
              style={{
                borderLeft: "5px solid #dc3545",
                paddingLeft: "12px",
                maxWidth: "700px",
              }}
            >
              <label>
                <strong>Fusion Management Fee Data Issue</strong>
              </label>
              <div style={{ fontSize: "13px", color: "#dc3545" }}>
                One or more selected sub-strategies is missing a management fee
                schedule for the selected tier. Each sub-strategy must have its
                own complete tier schedule before this section can be submitted.
              </div>
              <div
                style={{ fontSize: "13px", color: "#dc3545", marginTop: "6px" }}
              >
                {this.props.fusionFeeValidation.issues
                  .map((issue) => issue.message)
                  .join(" ")}{" "}
                Please correct the management fee table before completing
                Section 4.
              </div>
            </div>
          )}
      </div>
    );
  };

  renderFeeStageHeading = (text) => (
    <div
      className="mt-3 mb-3 px-3 py-2"
      style={{
        backgroundColor: "#385EA4",
        color: "#fff",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "14px",
      }}
    >
      {text}
    </div>
  );

  renderFeeSubheading = (text) => (
    <div className="form-group mb-2">
      <label className="mb-0">
        <strong>{text}</strong>
      </label>
    </div>
  );

  renderIarFlatInput = ({
    value,
    onChange,
    borderComplete,
    inputId = "iarFeeFlat-initial",
  }) => {
    const max = this.getIarFeeMax();
    return (
      <div className="form-group mb-0">
        <label htmlFor={inputId}>
          <strong>Fixed Percentage Rate</strong>
        </label>
        <div className="input-group" style={{ maxWidth: "200px" }}>
          <input
            type="number"
            id={inputId}
            className="form-control"
            style={{
              borderLeftColor: borderComplete ? "green" : "#ced4da",
              borderLeftWidth: "5px",
            }}
            value={value}
            onChange={(e) => {
              const val = e.target.value;
              if (
                val === "" ||
                (/^(?:0|[1-9]\d*)?(?:\.\d{0,4})?$/.test(val) &&
                  Number(val) <= max)
              ) {
                onChange(val);
              }
            }}
            onBlur={(e) => {
              const val = e.target.value;
              if (val !== "" && !isNaN(Number(val)))
                onChange(Number(val).toFixed(2));
            }}
            placeholder="0.00"
            step="0.0001"
            min="0"
            max={max}
          />
          <div className="input-group-append">
            <span className="input-group-text">%</span>
          </div>
        </div>
        {max < 2 && (
          <small className="form-text text-muted">
            Maximum IAR fee for Managed Money Market Solutions is {max}%.
          </small>
        )}
      </div>
    );
  };

  renderIarFeeBlock = ({
    type,
    flat,
    tiered,
    schedule,
    onType,
    onFlat,
    onTiered,
    onSchedule,
    feeSchedules,
    scheduleNames,
    disabled = false,
    radioGroupName = "iarFeeType",
    radioIdPrefix = "feeType",
    flatInputId = "iarFeeFlat",
    scheduleInputId = "iarFeeScheduleType",
  }) => {
    const max = this.getIarFeeMax();
    const currentScheduleLabels =
      feeSchedules[schedule] || feeSchedules[scheduleNames[0]] || [];
    const iarFeeTypeComplete =
      (type === "Fixed Percentage" && flat !== "") ||
      (type === "Tiered" &&
        normalizeIarFeeTiered(tiered, currentScheduleLabels).every(
          (t) => t.rate !== "",
        ));

    return (
      <div className="mb-3">
        <div className="form-group">
          <label>
            <strong>IAR Fee Type</strong>
          </label>
          <div
            style={{
              borderLeft: `5px solid ${iarFeeTypeComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={radioGroupName}
                id={`${radioIdPrefix}Flat`}
                value="Fixed Percentage"
                checked={type === "Fixed Percentage"}
                disabled={disabled}
                onChange={(e) => onType(e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label
                className="form-check-label"
                htmlFor={`${radioIdPrefix}Flat`}
              >
                Fixed Percentage
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={radioGroupName}
                id={`${radioIdPrefix}Tiered`}
                value="Tiered"
                checked={type === "Tiered"}
                disabled={disabled}
                onChange={(e) => onType(e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label
                className="form-check-label"
                htmlFor={`${radioIdPrefix}Tiered`}
              >
                Tiered
              </label>
            </div>
          </div>
        </div>

        {/* Fixed Percentage Fee */}
        {type === "Fixed Percentage" && (
          <div className="form-group mb-0">
            <label htmlFor={flatInputId}>
              <strong>Fixed Percentage Rate</strong>
            </label>
            <div className="input-group" style={{ maxWidth: "200px" }}>
              <input
                type="number"
                id={flatInputId}
                className="form-control"
                style={{
                  borderLeftColor: flat !== "" ? "green" : "#ced4da",
                  borderLeftWidth: "5px",
                }}
                value={flat}
                onChange={(e) => {
                  const val = e.target.value;
                  if (
                    val === "" ||
                    (/^(?:0|[1-9]\d*)?(?:\.\d{0,4})?$/.test(val) &&
                      Number(val) <= max)
                  ) {
                    onFlat(val);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val !== "" && !isNaN(Number(val))) {
                    onFlat(Number(val).toFixed(2));
                  }
                }}
                placeholder="0.00"
                step="0.0001"
                min="0"
                max={max}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {max < 2 && (
              <small className="form-text text-muted">
                Maximum IAR fee for Managed Money Market Solutions is {max}%.
              </small>
            )}
          </div>
        )}

        {/* Tiered Fee */}
        {type === "Tiered" &&
          (() => {
            const tieredRates = normalizeIarFeeTiered(
              tiered,
              currentScheduleLabels,
            );
            return (
              <div className="form-group mb-0">
                {scheduleNames.length > 1 && (
                  <div className="form-group">
                    <label htmlFor={scheduleInputId}>
                      <strong>Breakpoint Schedule</strong>
                    </label>
                    <select
                      id={scheduleInputId}
                      className="form-control"
                      value={schedule}
                      onChange={(e) => {
                        const newScheduleLabels =
                          feeSchedules[e.target.value] || [];
                        onTiered(
                          normalizeIarFeeTiered(null, newScheduleLabels),
                        );
                        if (onSchedule) onSchedule(e.target.value);
                      }}
                    >
                      {scheduleNames.map((name, index) => (
                        <option value={name} key={index}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <label>
                  <strong>Tiered Fee Schedule</strong>
                </label>
                <table
                  className="table table-sm table-bordered"
                  style={{ maxWidth: "450px" }}
                >
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th style={{ width: "120px" }}>Rate (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentScheduleLabels.map((label, index) => {
                      const rowRate = tieredRates[index]
                        ? tieredRates[index].rate
                        : "";
                      return (
                        <tr key={index}>
                          <td style={{ verticalAlign: "middle" }}>{label}</td>
                          <td>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                className="form-control"
                                value={rowRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^(?:0|[1-9]\d*)?(?:\.\d{0,4})?$/.test(
                                      val,
                                    ) &&
                                      Number(val) <= max)
                                  ) {
                                    onTiered(
                                      setTieredRate(
                                        tiered,
                                        currentScheduleLabels,
                                        index,
                                        val,
                                      ),
                                    );
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  if (val !== "" && !isNaN(Number(val))) {
                                    onTiered(
                                      setTieredRate(
                                        tiered,
                                        currentScheduleLabels,
                                        index,
                                        Number(val).toFixed(2),
                                      ),
                                    );
                                  }
                                }}
                                placeholder="0.00"
                                step="0.0001"
                                min="0"
                                max={max}
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">%</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
      </div>
    );
  };

  renderElementsPathFees = (feeSchedules, scheduleNames) => {
    if (this.props.managementFeesLoading) return this.renderStandardFees();

    const { values, onChange } = this.props;
    const fees = this.props.managementFees || {};
    const flatTable = this.renderFlatFeeTable();
    const hasTiers = fees?.tieredRates?.length > 0;
    const noteStyle = {
      fontSize: "12px",
      color: "#6c757d",
      fontWeight: 700,
      marginTop: "-8px",
    };

    return (
      <>
        {this.renderFeeStageHeading("Initial Stage Fees")}
        {this.renderFeeSubheading("IAR Fee")}
        {this.renderIarFeeBlock({
          type: values.iarFeeType || "Fixed Percentage",
          flat: values.iarFeeFlat,
          tiered: values.iarFeeTiered,
          schedule: values.iarFeeScheduleType,
          onType: (v) => onChange("iarFeeType", v),
          onFlat: (v) => onChange("iarFeeFlat", v),
          onTiered: (v) => onChange("iarFeeTiered", v),
          onSchedule: (v) => onChange("iarFeeScheduleType", v),
          feeSchedules,
          scheduleNames,
          disabled: false,
          radioGroupName: "initialIarFeeType",
          radioIdPrefix: "initialFeeType",
          flatInputId: "iarFeeFlat-initial",
          scheduleInputId: "initialIarFeeScheduleType",
        })}

        {this.renderFeeSubheading("Investment Management Fee")}
        {flatTable}
        {flatTable && fees?.minimumFee && (
          <div style={noteStyle}>
            {`Minimum account fee of $${fees.minimumFee} applies to the Fixed Percentage. The greater of the minimum or the Fixed Percentage will be charged to the account.`}
          </div>
        )}

        {this.renderFeeStageHeading("Path Strategy Fees")}
        {this.renderFeeSubheading("IAR Fee")}
        {this.renderIarFeeBlock({
          type: values.pathIarFeeType,
          flat: values.pathIarFeeFlat,
          tiered: values.pathIarFeeTiered,
          schedule: values.iarFeeScheduleType,
          onType: (v) => onChange("pathIarFeeType", v),
          onFlat: (v) => onChange("pathIarFeeFlat", v),
          onTiered: (v) => onChange("pathIarFeeTiered", v),
          onSchedule: (v) => onChange("iarFeeScheduleType", v),
          feeSchedules,
          scheduleNames,
          disabled: false,
          radioGroupName: "pathIarFeeType",
          radioIdPrefix: "pathFeeType",
          flatInputId: "pathIarFeeFlat",
          scheduleInputId: "pathIarFeeScheduleType",
        })}

        {this.renderFeeSubheading("Investment Management Fee")}
        {hasTiers && (
          <>
            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                fontStyle: "italic",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              After meeting the $25,000 threshold (as described in the Strategy
              Description), the Investment Management Fee converts to the tiered
              schedule shown below.
            </div>
            {this.renderFeeTable(
              fees.tieredRates,
              "Fee Schedule",
              "Program Fee - Read Only",
            )}
          </>
        )}
        {hasTiers && fees?.pathMinimumFee && (
          <div style={noteStyle}>
            {`After meeting the $25,000 threshold, a minimum account fee of $${fees.pathMinimumFee} applies. The greater of the minimum account fee or the Investment Management Fee percentage will be charged to the account.`}
          </div>
        )}
      </>
    );
  };

  render() {
    const { values, onChange } = this.props;

    const tierOptions = this.props.managementFeeTierOptions || [];
    const feeSchedules = this.getFeeSchedules();
    const scheduleNames = Object.keys(feeSchedules);
    const mgmt = this.props.managementFees;
    const isFlat = !!(mgmt && mgmt.flatFee != null);
    const hasPath = !!this.props.hasPath;
    const isElementsPathMode = isFlat && hasPath;

    // Only show dropdown if there are 2+ tiers to choose from
    const showTierDropdown = tierOptions.length > 1;
    // The "Investment Management Fee Tier" subsection is hidden only when
    // there is nothing to choose: Standard is the sole available tier. This
    // is driven by the user's Evolution tiers and applies equally to Fusion,
    // which still resolves a tier even though its fee schedules are shown
    // per-sub-strategy.
    const hideTierSection =
      tierOptions.length === 1 && tierOptions[0].label === "Standard";
    const tierHelperText = !this.props.strategy
      ? "Please select a strategy in Section 2 to see available fee tiers."
      : tierOptions.length === 0
        ? "No fee tiers are available for the selected strategy and your Evolution tiers."
        : null;

    return (
      <div>
        {!hideTierSection && (
          <div className="form-group">
            <label htmlFor="iarFeeTier">
              <strong>Investment Management Fee Tier</strong>
            </label>
            {tierOptions.length > 0 ? (
              showTierDropdown ? (
                <select
                  id="iarFeeTier"
                  className="form-control"
                  style={{
                    borderLeftColor: values.iarFeeTier ? "green" : "#ced4da",
                    borderLeftWidth: "5px",
                  }}
                  value={values.iarFeeTier}
                  onChange={(e) => onChange("iarFeeTier", e.target.value)}
                >
                  <option value="">Select Fee Tier</option>
                  {tierOptions.map((option) => (
                    <option value={option.label} key={option.exactTier}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: "15px", color: "#495057" }}>
                  {tierOptions[0].label}
                </div>
              )
            ) : (
              <div
                style={{
                  fontSize: "13px",
                  color: this.props.strategy ? "#6c757d" : "#495057",
                  borderLeft: "5px solid #ced4da",
                  paddingLeft: "12px",
                }}
              >
                {tierHelperText}
              </div>
            )}
          </div>
        )}

        {/* Investment Management Fee — standard or Fusion sub-strategy
            schedules. Both wait for a resolved fee tier: when the subsection
            is hidden (Standard-only) iarFeeTier is auto-set, so this renders
            immediately; when a tier dropdown is shown it waits for a pick. */}
        {this.props.strategy &&
          values.iarFeeTier &&
          (this.props.isFusion
            ? this.renderFusionFees()
            : isElementsPathMode
              ? this.renderElementsPathFees(feeSchedules, scheduleNames)
              : this.renderStandardFees())}

        {!isElementsPathMode && (
          <>
            {/* Divider: investment management fee above, IAR fee below */}
            <hr style={{ borderTop: "1px solid #dee2e6", margin: "20px 0" }} />

            {/* IAR Fee — three modes:
            1. normal (no flatFee): one block with Flat/Tiered radio (unchanged).
            2. flat IM + no path (MMMS-shaped): radio disabled, flat only.
            3. flat IM + path (Elements-shaped): two stacked sub-blocks with
               stage headers; initial has no radio; path has the full radio. */}
            {(() => {
              if (isFlat && !hasPath) {
                return this.renderIarFeeBlock({
                  type: "Fixed Percentage",
                  flat: values.iarFeeFlat,
                  tiered: [],
                  schedule: values.iarFeeScheduleType,
                  onType: () => {},
                  onFlat: (v) => onChange("iarFeeFlat", v),
                  onTiered: () => {},
                  onSchedule: (v) => onChange("iarFeeScheduleType", v),
                  feeSchedules,
                  scheduleNames,
                  disabled: true,
                });
              }
              return this.renderIarFeeBlock({
                type: values.iarFeeType,
                flat: values.iarFeeFlat,
                tiered: values.iarFeeTiered,
                schedule: values.iarFeeScheduleType,
                onType: (v) => onChange("iarFeeType", v),
                onFlat: (v) => onChange("iarFeeFlat", v),
                onTiered: (v) => onChange("iarFeeTiered", v),
                onSchedule: (v) => onChange("iarFeeScheduleType", v),
                feeSchedules,
                scheduleNames,
                disabled: false,
              });
            })()}
          </>
        )}

        {/* Zero fee waiver fine print (shown after user confirmed) */}
        {values.iarFeeWaiver && (
          <div
            className="mt-2"
            style={{ fontSize: "11px", color: "#6c757d", fontStyle: "italic" }}
          >
            Fee waiver acknowledged on {values.iarFeeWaiver.confirmedAt} by{" "}
            {values.iarFeeWaiver.confirmedBy}. Zero-rate fields:{" "}
            {values.iarFeeWaiver.zeroFields.join(", ")}.
          </div>
        )}
      </div>
    );
  }
}

export default ISPFeeSchedule;
